import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { adaptReviewToUI } from 'src/adapters/storeToUI/adaptReviewToUI';
import { trackReviewCardControlClick } from 'src/components/organisms/CustomersReviewsPreview/tracking/customersReviewsPreviewEvents';
import { getReviewsDrawerHref } from 'src/fragmentIDs';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectProductPartner } from 'src/redux/product/selectors';
import {
  selectPhotoReviews,
  selectRatingBreakdown,
  selectReviewsList,
  selectReviewsTotalCount,
} from 'src/redux/reviews/selectors';
import { actions as reviewsActions } from 'src/redux/reviews/slice';
import { requestReviewsPage } from 'src/redux/reviews/thunks';
import type { ReviewRatingFilter } from 'src/redux/reviews/types';
import type { AppThunkDispatch } from 'src/redux/store';
import type { CustomersReviewsDrawerProps } from '../components/CustomersReviewsDrawer';
import {
  trackDrawerOpen,
  trackNextPageClick,
  trackPaginationClick,
  trackPreviousPageClick,
  trackRatingFilter,
  trackRatingSystemInfoClick,
  trackSharedReviewOpen,
} from '../tracking/customersReviewsDrawerEvents';
import { useReviewsDrawerFragment } from './useReviewsDrawerFragment';

/** Everything the drawer needs but the column it aligns to, which the layout owns. */
export type UseCustomersReviewsDrawer = Omit<CustomersReviewsDrawerProps, 'target'>;

/**
 * Wires the reviews drawer to the store, the URL and tracking, so the drawer stays pure
 * props and the layout composes it like its other parts. The first page arrives with the
 * page's own reviews fetch, which also carries the summary, so only further pages are
 * requested from here, once a customer asks for them.
 */
export const useCustomersReviewsDrawer = (): UseCustomersReviewsDrawer => {
  const dispatch = useDispatch() as AppThunkDispatch;
  const { clearReviewId, close, isOpen, reviewId } = useReviewsDrawerFragment();
  const list = useSelector(selectReviewsList);
  const photoReviews = useSelector(selectPhotoReviews);
  const totalCount = useSelector(selectReviewsTotalCount);
  const ratingBreakdown = useSelector(selectRatingBreakdown);
  const partner = useSelector(selectProductPartner);
  const productCode = useSelector((state: ReduxApplicationState) => state.product.code);

  // The page asked for, not the one that last arrived, so a click changes the screen at once.
  const page = list.visiblePage;
  const storeReviews = list.pages[page];

  // Once per opening, not on every render, page or filter change.
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedOpen.current = false;

      return;
    }

    if (!hasTrackedOpen.current) {
      hasTrackedOpen.current = true;
      trackDrawerOpen();

      if (reviewId) {
        trackSharedReviewOpen(reviewId);
      }
    }
  }, [isOpen, reviewId]);

  const reviews = useMemo(
    () => storeReviews?.map((review) => adaptReviewToUI(review, partner)) ?? null,
    [partner, storeReviews],
  );

  // Only shown when it is on the loaded page or among the reviews with photos, which the
  // customers' photos strip links to and which are mostly far down the list: Feefo cannot
  // fetch one review by id, and paging blind for it would be unbounded, so any other link to
  // an older review opens at the top of the list.
  const linkedReview = useMemo(() => {
    if (!reviewId) {
      return null;
    }

    const onPage = reviews?.find(({ id }) => id === reviewId);

    if (onPage) {
      return onPage;
    }

    const withPhotos = photoReviews?.find(({ id }) => id === reviewId);

    return withPhotos ? adaptReviewToUI(withPhotos, partner) : null;
  }, [partner, photoReviews, reviewId, reviews]);

  // A review link points at the loaded first page (the page's preview cards show page 1), so
  // a drawer left on a later page goes back to it rather than opening without its review.
  // Judged once the visible page has loaded and only when the review is not on it, so a card
  // opened from page 3 of the list stays on page 3.
  useEffect(() => {
    if (isOpen && reviewId && storeReviews && !linkedReview && page !== 1 && list.pages[1]) {
      dispatch(reviewsActions.showPage(1));
    }
  }, [dispatch, isOpen, linkedReview, list.pages, page, reviewId, storeReviews]);

  // A review link from the preview row, which is never filtered, can point at a review that
  // a rating left in the drawer from an earlier visit hides. The filter is dropped rather than
  // the review searched for: Feefo cannot fetch one review by id. Judged once the filtered
  // page has loaded and only when the review is not on it: the drawer's own cards carry the
  // same links, and a card opened from a filtered list must keep its filter.
  useEffect(() => {
    if (
      isOpen &&
      reviewId &&
      list.rating !== null &&
      productCode &&
      storeReviews &&
      !linkedReview &&
      (page === 1 || !list.pages[1])
    ) {
      dispatch(requestReviewsPage({ page: 1, productCode, rating: null }));
    }
  }, [dispatch, isOpen, linkedReview, list.rating, productCode, reviewId, storeReviews]);

  const goToPage = (nextPage: number, rating: ReviewRatingFilter) => {
    // The URL stops claiming a review that is no longer on screen.
    if (reviewId) {
      clearReviewId();
    }

    // Already held for this filter, so show it rather than asking again.
    if (rating === list.rating && list.pages[nextPage]) {
      dispatch(reviewsActions.showPage(nextPage));

      return;
    }

    if (productCode) {
      dispatch(requestReviewsPage({ page: nextPage, productCode, rating }));
    }
  };

  // The same event as the preview cards, so both surfaces report under one name.
  const handleControlClick: NonNullable<CustomersReviewsDrawerProps['onControlClick']> = (
    _e,
    { control },
  ) => trackReviewCardControlClick(control);

  const handlePageChange = (nextPage: number, trigger: 'next' | 'page' | 'previous') => {
    goToPage(nextPage, list.rating);

    switch (trigger) {
      case 'next':
        trackNextPageClick(nextPage);
        break;
      case 'previous':
        trackPreviousPageClick(nextPage);
        break;
      default:
        trackPaginationClick(nextPage);
    }
  };

  const handleRatingChange = (rating: ReviewRatingFilter) => {
    // Always back to page 1: page 4 of all ratings has no counterpart in the 3-star list.
    goToPage(1, rating);
    trackRatingFilter(rating);
  };

  return {
    activeRating: list.rating,
    error: list.error,
    hrefForReview: getReviewsDrawerHref,
    isOpen,
    linkedReview,
    onBack: clearReviewId,
    onClose: close,
    onControlClick: handleControlClick,
    onPageChange: handlePageChange,
    onRatingChange: handleRatingChange,
    onRatingSystemInfoClick: trackRatingSystemInfoClick,
    page,
    ratingBreakdown: ratingBreakdown ?? null,
    reviews,
    totalCount,
    totalPages: list.meta?.totalPages ?? 1,
  };
};
